import { useEffect, useState, useCallback } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase.js';
import { useAuth } from './auth.js';
import { DEFAULT_HOME_LAYOUT, SECURITY_GROUP, ENTITY_DOCUMENTS_GROUP } from './home-defaults.js';
import { LAYOUT_VERSION, type HomeLayout, type SidebarSection } from './home-schema.js';

const LOCAL_KEY = 'sfp.homeLayout.local.v1';
const DEMO_MODE = import.meta.env.VITE_SFP_DEMO_MODE === 'true';

/**
 * Per-user layout, hot-loaded from Firestore (`homeLayouts/{uid}`),
 * with a localStorage fallback so the home page renders instantly
 * on cold load and keeps working when offline / before the user
 * doc exists.
 */
export function useHomeLayout() {
  const { user } = useAuth();
  const [layout, setLayout] = useState<HomeLayout>(() => readLocal() ?? DEFAULT_HOME_LAYOUT);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || DEMO_MODE) {
      setLayout(readLocal() ?? DEFAULT_HOME_LAYOUT);
      setLoaded(true);
      return;
    }
    const ref = doc(db, 'homeLayouts', user.uid);
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(ref);
        if (cancelled) return;
        if (snap.exists()) {
          const stored = snap.data() as HomeLayout;
          const merged = migrate(stored);
          setLayout(merged);
          writeLocal(merged);
        } else {
          setLayout(DEFAULT_HOME_LAYOUT);
        }
      } catch (err) {
        console.warn('[home-store] read failed, falling back to local/default', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const stored = snap.data() as HomeLayout;
      const merged = migrate(stored);
      setLayout(merged);
      writeLocal(merged);
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [user]);

  const save = useCallback(
    async (next: HomeLayout) => {
      setLayout(next);
      writeLocal(next);
      if (!user || DEMO_MODE) return;
      setSaving(true);
      try {
        await setDoc(doc(db, 'homeLayouts', user.uid), next);
      } catch (err) {
        console.error('[home-store] save failed', err);
      } finally {
        setSaving(false);
      }
    },
    [user],
  );

  const resetToDefault = useCallback(() => {
    void save(DEFAULT_HOME_LAYOUT);
  }, [save]);

  return { layout, save, resetToDefault, loaded, saving };
}

function migrate(stored: HomeLayout): HomeLayout {
  // Self-healing migration. Preserve the user's customizations, but guarantee
  // that EVERY navigation section/item shipped in the current default exists in
  // the user's saved layout. New nav items (Integrations → Dropbox, File
  // Explorer, Trading, Admin, Security, …) were added after some per-user
  // layouts were already saved, so they never appeared — the sidebar renders
  // strictly from the saved layout. We merge by id (never duplicate, never
  // remove the user's own sections/items), then stamp the current version.
  // Anything the user explicitly deleted is never re-added.
  const dismissed = new Set(stored.dismissedNav ?? []);

  const sidebar = mergeSidebar(stored.sidebar ?? [], DEFAULT_HOME_LAYOUT.sidebar, dismissed);

  // Home-base tile groups: keep the user's; re-add the Security folder only if
  // it's missing AND the user hasn't deliberately deleted it. Other tile groups
  // stay fully user-controlled.
  let groups = [...(stored.groups ?? [])];
  const hasSecurityGroup = groups.some((g) => g.id === SECURITY_GROUP.id);
  if (!hasSecurityGroup && !dismissed.has(SECURITY_GROUP.id)) {
    groups = [...groups, SECURITY_GROUP];
  }

  // Inject the dynamic Entity Documents group just after Documents (unless the
  // user deleted it). It renders one tile per entity at runtime.
  const hasEntityDocs = groups.some((g) => g.id === ENTITY_DOCUMENTS_GROUP.id);
  if (!hasEntityDocs && !dismissed.has(ENTITY_DOCUMENTS_GROUP.id)) {
    const docIdx = groups.findIndex((g) => g.id === 'documents-group');
    if (docIdx >= 0) groups.splice(docIdx + 1, 0, ENTITY_DOCUMENTS_GROUP);
    else groups.push(ENTITY_DOCUMENTS_GROUP);
  } else {
    // Ensure the stored copy is flagged dynamic (older saves had an empty group).
    groups = groups.map((g) =>
      g.id === ENTITY_DOCUMENTS_GROUP.id ? { ...g, dynamic: 'entities' as const } : g,
    );
  }

  // Add the Reclassify tile to the Documents group if missing (and not dismissed).
  groups = groups.map((g) => {
    if (g.id !== 'documents-group') return g;
    const hasReclassify = g.tiles.some((t) => t.id === 'reclassify' || t.href === '/documents/reclassify');
    if (hasReclassify || dismissed.has('reclassify')) return g;
    const exportIdx = g.tiles.findIndex((t) => t.id === 'export');
    const tile = { id: 'reclassify', label: 'Reclassify', color: 'terracotta' as const, glyph: '⇄', href: '/documents/reclassify' };
    const tiles = [...g.tiles];
    tiles.splice(exportIdx >= 0 ? exportIdx : tiles.length, 0, tile);
    return { ...g, tiles };
  });

  return { ...stored, groups, sidebar, version: LAYOUT_VERSION };
}

/**
 * Merge the default sidebar into the user's saved sidebar, by id:
 *  - any default section the user is missing is appended;
 *  - within a section the user already has, any default item the user is
 *    missing is appended (matched by id OR href so renamed-id duplicates of the
 *    same destination don't pile up);
 *  - the user's order, custom sections, and custom items are all preserved.
 */
function mergeSidebar(
  userSidebar: SidebarSection[],
  defaultSidebar: SidebarSection[],
  dismissed: Set<string>,
): SidebarSection[] {
  const merged: SidebarSection[] = userSidebar.map((s) => ({ ...s, items: [...(s.items ?? [])] }));

  for (const defSec of defaultSidebar) {
    // The user deleted this whole default section — leave it gone.
    if (dismissed.has(defSec.id)) continue;

    const existing = merged.find((s) => s.id === defSec.id);
    if (!existing) {
      merged.push({
        ...defSec,
        items: defSec.items.filter((it) => !dismissed.has(it.id)),
      });
      continue;
    }
    const haveIds = new Set(existing.items.map((i) => i.id));
    const haveHrefs = new Set(existing.items.map((i) => i.href));
    for (const it of defSec.items) {
      // Skip items the user deleted, or that already exist (by id or destination).
      if (dismissed.has(it.id) || haveIds.has(it.id) || haveHrefs.has(it.href)) continue;
      existing.items.push(it);
      haveIds.add(it.id);
      haveHrefs.add(it.href);
    }
  }
  return merged;
}

function readLocal(): HomeLayout | null {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HomeLayout;
    if (parsed.version !== LAYOUT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocal(layout: HomeLayout) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(layout));
  } catch {
    /* ignore */
  }
}
